<template>
    <div>
        <section class="intro-box" v-if="infoData.introCn">
            <div class="intro">{{ infoData.introCn }}</div>
        </section>
        
        <section 
            class="information-content" 
            v-html="sanitizedContent" 
            @click.stop="onClick" 
            ref="contentRef"
        ></section>
        
        <p class="information-desc" v-html="infoData.disclaimers"></p>

        <van-image-preview
            v-model="visibleFlag"
            :start-position="index"
            :images="images"
            @change="handleChange"
            :loop="false"
            class="news-images"
            :maxZoom="10"
        ></van-image-preview>
    </div>
</template>

<script>
import { onMounted, watch, toRefs, computed } from 'vue'
import DOMPurify from 'dompurify'
import { useImagePreview } from '../composables/useImagePreview'

export default {
    name: 'ArticleBody',
    props: {
        infoData: {
            type: Object,
            default: () => ({})
        },
        jsBridge: {
            type: Object,
            default: null
        },
        isTHSApp: {
            type: Function,
            default: () => false
        }
    },
    setup(props) {
        const { infoData } = toRefs(props)
        const {
            visibleFlag,
            index,
            images,
            contentRef,
            initImages,
            handleChange,
            handleImageClick
        } = useImagePreview()

        const sanitizedContent = computed(() =>
            DOMPurify.sanitize(infoData.value?.contentText || '', {
                FORBID_TAGS: ['u']
            })
        )

        // Watch infoData changes to re-init images
        watch(() => infoData.value.contentText, () => {
            initImages()
        })

        watch(visibleFlag, (val) => {
            const cls = 'news-images-overlay'
            const el = document.body
            if (!el) return
            if (val) el.classList.add(cls)
            else el.classList.remove(cls)
        })

        const onClick = (e) => {
            const target = e.target

            // Stock Link
            const stockId = target.getAttribute('data-stock-id')
            if (stockId) {
                // eslint-disable-next-line no-unused-expressions
                props.jsBridge && props.jsBridge.goPage(`stockQuote?stockId=${stockId}`)
                return
            }

            // Image
            if (target.tagName.toLowerCase() == 'img') {
                handleImageClick(target)
            }

            // PDF Link
            const aHref = target.getAttribute && target.getAttribute('href')
            if (target.tagName.toLowerCase() === 'a' && typeof aHref === 'string' && aHref.indexOf('fhzqwebview=pdf') >= 0) {
                // eslint-disable-next-line vars-on-top
                var isAndroid = /android/gi.test(window.navigator.appVersion || '')
                if (props.isTHSApp() && isAndroid) {
                    // eslint-disable-next-line no-undef
                    callNativeHandler(
                        'HXDownloadFile',
                        JSON.stringify({
                            url: aHref,
                            filetype: 'pdf',
                            title: 'pdf',
                        }),
                        true
                    )
                    e.preventDefault()
                    e.stopPropagation ? e.stopPropagation() : (e.cancelBubble = true)
                    return false
                }
            }
        }

        onMounted(() => {
            initImages()
        })

        return {
            contentRef,
            visibleFlag,
            index,
            images,
            handleChange,
            onClick,
            sanitizedContent
        }
    }
}
</script>

<style lang="less">
@import url('../css/mixins.less');

.news-images-overlay .van-overlay {
    background-color: rgba(0, 0, 0, 1) !important;
}

.information-content table {
    width: 100% !important;
}

.hn-scroll-area {
    width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    touch-action: pan-x pan-y !important;
    overscroll-behavior-x: contain;
    margin: 12px 0;
    scroll-behavior: smooth;
    position: relative;
    z-index: 1;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: 768px) {
        -webkit-overflow-scrolling: touch;
        touch-action: pan-x pan-y !important;
    }
}

table {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    white-space: nowrap;
}

th, td {
    padding: 8px;
    text-align: left;
    border: 1px solid #ddd;
    white-space: nowrap;
    min-width: 80px;
}

th {
    background-color: #f2f2f2;
}

.information-content {
    p:not(:last-child) {
        margin-bottom: @p_margin_bottom;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 180%;
    }
    h1, h2, h3, h4, h5, h6 {
        margin-bottom: @p_margin_bottom;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        #news_title();
    }
    blockquote {
        margin-bottom: @p_margin_bottom;
    }
    img {
        width: 100%;
    }
    ol {
        list-style: decimal !important;
    }
    ul {
        list-style: disc !important;
    }
    ul, ol {
        padding-left: 1.5em;
        margin-left: 0;
        list-style-position: outside;
        margin-bottom: @p_margin_bottom;
    }
    li::marker {
        position: relative;
        left: 0.5em;
    }
    @media (max-width: 414px) {
        ul, ol {
            padding-left: 1.35em;
        }
        li::marker {
            left: 0.45em;
        }
    }
    @media (max-width: 375px) {
        ul, ol {
            padding-left: 1.25em;
        }
        li::marker {
            left: 0.4em;
        }
    }
}
</style>

<style lang="less" scoped>
@import url('../css/mixins.less');

.intro-box {
    display: flex;
    padding: 12px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius: 12px;
    background: #f7f7f7;
    margin-top: 16px;
    #intro_background();
    .intro {
        color: #181818;
        text-align: justify;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 160%;
        #intro_content();
    }
}

.information-content {
    margin: 16px 0 12px;
    font-size: 16px;
    line-height: 30px;
    line-height: 1.7;
    text-align: justify;
    word-wrap: break-word;
    word-break: break-all;
    #news_content();
}

.information-desc {
    padding: 0 0 12px;
    color: #9c9c9c;
    text-align: justify;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    font-style: normal;
    #news_desc();
}
</style>
